import * as React from 'react';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import { TableBody, TableCell, TableHead } from '@mui/material';
import { TableComponents, TableVirtuoso, TableVirtuosoHandle } from 'react-virtuoso';
import { HeaderCell } from './Components/TableHeader/styles';
import { useCallback, useEffect, useRef } from 'react';
import { TableHeader } from './Components/TableHeader/TableHeader';
import Body from '@mui/material/TableBody';
import { useScrollStore } from '../../Stores/useGeneralAppStore';

interface DashboardTableProps {
  headerCells: HeadCell[];
  loadMore: () => void;
  hasMore: boolean;
  rows: any[];
  rowContent: any;
  totalItems: number;
  pageNavigation?: string;
}

const virtuosoTableComponents: TableComponents<any> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer
      component={Paper}
      {...props}
      ref={ref}
      sx={{ height: '100%', minHeight: '400px' }}
    />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed', width: '100%' }} />
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

export const DashboardScrollTable = ({
  headerCells,
  loadMore,
  hasMore,
  rows,
  rowContent,
  totalItems,
  pageNavigation,
}: DashboardTableProps) => {
  const { sourcePage, selectedScrollPosition, setSelectedSourcePage } = useScrollStore();
  const virtuoso = useRef<TableVirtuosoHandle>(null);

  const FixedHeaderContent = useCallback(() => {
    return (
      <TableRow>
        {headerCells.map((headCell) => (
          <HeaderCell key={headCell.id}>{headCell.label}</HeaderCell>
        ))}
      </TableRow>
    );
  }, [headerCells]);

  useEffect(() => {
    if (selectedScrollPosition === null) {
      return;
    }

    if (!pageNavigation || !sourcePage || pageNavigation !== sourcePage) {
      setSelectedSourcePage('');
      return;
    }

    const timer = setTimeout(() => {
      virtuoso.current?.scrollToIndex({
        index: selectedScrollPosition,
        align: 'center',
        behavior: 'smooth',
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [
    pageNavigation,
    selectedScrollPosition,
    setSelectedSourcePage,
    sourcePage,
    rows,
    hasMore,
    loadMore,
  ]);

  return (
    <>
      <Paper sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {rows.length > 0 ? (
          <React.Fragment>
            <TableVirtuoso
              ref={virtuoso}
              context={rows}
              style={{ flex: 1 }}
              data={rows}
              endReached={hasMore ? loadMore : undefined}
              fixedHeaderContent={FixedHeaderContent}
              increaseViewportBy={200}
              components={virtuosoTableComponents}
              itemContent={(index, row) => rowContent(index, row)}
            />
            <p style={{ textAlign: 'end', padding: '10px' }}>Total items: {totalItems}</p>
          </React.Fragment>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'medium'}>
              <TableHeader headCells={headerCells} />
              <Body>
                <TableRow>
                  <TableCell colSpan={headerCells.length} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              </Body>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  );
};
